import React, { useState, useEffect, useCallback, ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { Session } from '@/types'
import { supabase } from '../../../lib/supabase'
import { ensureProfileExists } from '../../../lib/profileUtils'
import CustomConfirmModal from '../../common/CustomConfirmModal'
import HistoryTable from './HistoryTable'
import styles from './RequestAccessSection.module.css'

type ShareStatus = 'pending' | 'accepted' | 'rejected'
type HistoryAction = 'granted' | 'accepted' | 'rejected' | 'removed' | 'forgotten'

interface OwnerProfile {
    email?: string
    full_name?: string | null
    bio?: string | null
}

interface LibraryShare {
    id: string
    owner_id: string
    viewer_id: string
    info_shared_to_email: string
    status: ShareStatus
    message: string
    owner_profile?: OwnerProfile | OwnerProfile[]
}

interface ShareHistoryNotification {
    id: string
    owner_id: string
    biblionaut_id: string
    target_email?: string
    action: HistoryAction
    message?: string | null
    created_at: string
    owner_profile?: OwnerProfile
}

interface NotificationState {
    isOpen: boolean
    title: string
    message: string
    type: 'success' | 'danger' | 'warning' | 'info'
}

interface RequestAccessSectionProps {
    session: Session
    onViewLibrary: (userId: string, userName: string) => void
}

export default function RequestAccessSection({ session, onViewLibrary }: Readonly<RequestAccessSectionProps>) {
    const { t } = useTranslation()
    const [email, setEmail] = useState('')
    const [message, setMessage] = useState('')
    const [requests, setRequests] = useState<LibraryShare[]>([])
    const [notifications, setNotifications] = useState<ShareHistoryNotification[]>([])
    const [loading, setLoading] = useState(true)
    const [requestToDelete, setRequestToDelete] = useState<string | null>(null)
    const [lastSeen, setLastSeen] = useState<string | null>(null)

    const [notification, setNotification] = useState<NotificationState>({
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
    })

    const showNotify = (title: string, message: string, type: NotificationState['type'] = 'info') => {
        setNotification({ isOpen: true, title, message, type })
    }

    const fetchSettings = useCallback(async () => {
        try {
            const { data } = await supabase
                .from('shared_library_settings')
                .select('last_seen_requests_at')
                .eq('user_id', session.user.id)
                .single()
            setLastSeen(data?.last_seen_requests_at || '1970-01-01T00:00:00Z')
        } catch (error) {
            console.error('Error fetching settings:', error)
        }
    }, [session.user.id])

    const fetchRequests = useCallback(async () => {
        try {
            const { data, error } = await supabase
                .from('library_shares')
                .select('*, owner_profile:owner_id(email, full_name, bio)')
                .or(`viewer_id.eq.${session.user.id},info_shared_to_email.eq.${session.user.email}`)
                .neq('status', 'rejected')

            if (error) throw error
            setRequests((data as LibraryShare[]) || [])
        } catch (error) {
            console.error('Error fetching requests:', error)
        } finally {
            setLoading(false)
        }
    }, [session.user.id, session.user.email])

    const fetchNotifications = useCallback(async () => {
        // Implemented in useEffect below
    }, [])

    useEffect(() => {
        const init = async () => {
            await fetchSettings()
            await fetchRequests()
            await fetchNotifications()
        }
        init()
    }, [fetchSettings, fetchRequests, fetchNotifications])

    useEffect(() => {
        if (!lastSeen) return
        const getNotes = async () => {
            try {
                const { data } = await supabase
                    .from('library_share_history')
                    .select('*, owner_profile:owner_id(full_name, email)')
                    .eq('biblionaut_id', session.user.id)
                    .in('action', ['granted', 'accepted', 'rejected', 'removed'])
                    .gt('created_at', lastSeen)
                    .order('created_at', { ascending: false })

                setNotifications((data as ShareHistoryNotification[]) || [])
            } catch (e) {
                console.error(e)
            }
        }
        getNotes()
    }, [lastSeen, session.user.id])

    const handleRequestAccess = async (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault()
        try {
            const profileReady = await ensureProfileExists(session)
            if (!profileReady) {
                showNotify('Error', t('biblionautas.request.notifies.profileError'), 'danger')
                return
            }

            const { data: profiles, error: profileError } = await supabase.from('profiles').select('id').eq('email', email).single()

            if (profileError || !profiles) {
                showNotify(
                    'Error',
                    t('biblionautas.request.notifies.userNotFound'),
                    'warning'
                )
                return
            }

            const ownerId = profiles.id

            const { data: existing } = await supabase
                .from('library_shares')
                .select('id, status')
                .eq('owner_id', ownerId)
                .eq('viewer_id', session.user.id)
                .maybeSingle()

            if (existing) {
                if (existing.status === 'rejected') {
                    showNotify(
                        t('biblionautas.request.notifies.accessDenied.title'),
                        t('biblionautas.request.notifies.accessDenied.message'),
                        'danger'
                    )
                } else if (existing.status === 'accepted') {
                    showNotify(t('biblionautas.request.notifies.alreadyAccess.title'), t('biblionautas.request.notifies.alreadyAccess.message'), 'info')
                } else {
                    showNotify(t('biblionautas.request.notifies.pending.title'), t('biblionautas.request.notifies.pending.message'), 'info')
                }
                return
            }

            const { data: historyEvents } = await supabase
                .from('library_share_history')
                .select('action')
                .eq('owner_id', ownerId)
                .eq('biblionaut_id', session.user.id)
                .order('created_at', { ascending: false })
                .limit(1)

            if (historyEvents && historyEvents.length > 0) {
                const lastAction = historyEvents[0].action
                if (lastAction === 'rejected' || lastAction === 'removed') {
                    showNotify(
                        t('biblionautas.request.notifies.blocked.title'),
                        t('biblionautas.request.notifies.blocked.message'),
                        'danger'
                    )
                    return
                }
            }

            const { error } = await supabase.from('library_shares').insert({
                owner_id: ownerId,
                viewer_id: session.user.id,
                info_shared_to_email: session.user.email,
                status: 'pending',
                message: message,
            })

            if (error) throw error

            setEmail('')
            setMessage('')
            fetchRequests()
            showNotify('Éxito', t('biblionautas.request.notifies.success'), 'success')
        } catch (err) {
            console.error('Error requesting access:', err)
            showNotify('Error', t('biblionautas.request.notifies.error'), 'danger')
        }
    }

    const handleDelete = async (id: string) => {
        setRequestToDelete(id)
    }

    const confirmDelete = async () => {
        if (!requestToDelete) return
        const id = requestToDelete
        setRequestToDelete(null)

        const req = requests.find((r) => r.id === id)
        if (req) {
            try {
                await supabase.from('library_share_history').insert({
                    owner_id: req.owner_id,
                    biblionaut_id: session.user.id,
                    target_email: req.info_shared_to_email,
                    action: 'forgotten',
                    message: '',
                })
            } catch (err) {
                console.error('Error logging forgotten action:', err)
            }
        }

        await supabase.from('library_shares').delete().eq('id', id)
        fetchRequests()
    }

    const getNotificationText = (note: ShareHistoryNotification) => {
        const ownerName = note.owner_profile?.email || 'Una biblioteca'
        switch (note.action) {
            case 'granted':
                return t('biblionautas.request.history.granted', { name: ownerName })
            case 'accepted':
                return t('biblionautas.request.history.accepted', { name: ownerName })
            case 'rejected':
                return t('biblionautas.request.history.rejected', { name: ownerName })
            case 'removed':
                return t('biblionautas.request.history.removed', { name: ownerName })
            default:
                return t('biblionautas.request.history.default', { name: ownerName })
        }
    }

    const pendingRequests = requests.filter((r) => r.status === 'pending')
    const myAccesses = requests.filter((r) => r.status === 'accepted')

    const getProfileData = (req: LibraryShare): OwnerProfile => {
        const profile = Array.isArray(req.owner_profile) ? req.owner_profile[0] : req.owner_profile
        return profile || {}
    }

    return (
        <div className={`${styles.biblioSection} fade-in`}>
            <h3>{t('biblionautas.request.title')}</h3>

            {/* Notifications Block */}
            {notifications.length > 0 && (
                <div className={styles.notificationsBlock} data-testid="notifications-block">
                    <h4>{t('biblionautas.request.notificationsTitle')}</h4>
                    {notifications.map((note) => (
                        <div key={note.id} className={styles.notificationItem} data-testid={`notification-${note.id}`}>
                            <div>{getNotificationText(note)}</div>
                            {note.message && (
                                <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>"{note.message}"</div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <div className={styles.accessFormCard} data-testid="request-access-form">
                <h4>{t('biblionautas.request.requestAccess.title')}</h4>
                <form onSubmit={handleRequestAccess} className={styles.biblioForm}>
                    <div className={styles.formGroup}>
                        <label>{t('biblionautas.request.requestAccess.emailLabel')}</label>
                        <input type="email" value={email} onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} required data-testid="request-email-input" />
                    </div>
                    <div className={styles.formGroup}>
                        <label>{t('biblionautas.request.requestAccess.messageLabel')}</label>
                        <textarea value={message} onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)} data-testid="request-message-input" />
                    </div>
                    <button type="submit" className="btn-primary" data-testid="request-submit-btn">
                        {t('biblionautas.request.requestAccess.submit')}
                    </button>
                </form>
            </div>

            <div className="shares-list">
                <h4>{t('biblionautas.request.myRequests.title')}</h4>
                {(() => {
                    if (loading) return <p>Cargando...</p>
                    if (requests.length === 0) return <p className="empty-state">{t('biblionautas.request.myRequests.empty')}</p>
                    return (
                        <>
                            {/* Pending Requests */}
                            {pendingRequests.length > 0 && (
                                <div className="subsection" data-testid="pending-requests-section">
                                    <h5 className={styles.subsectionTitle}>{t('biblionautas.request.myRequests.pendingTitle')}</h5>
                                    <div className={styles.shareCardsGrid}>
                                        {pendingRequests.map((req) => {
                                            const profile = getProfileData(req)
                                            return (
                                                <div key={req.id} className="share-card" data-testid={`pending-request-card-${req.owner_id}`}>
                                                    <div className="share-header">
                                                        <div className="share-user-info">
                                                            <span className="share-name" style={{ fontWeight: 'bold', display: 'block' }}>
                                                                {profile.full_name || 'Usuario'}
                                                            </span>
                                                            <span className="share-email" style={{ fontSize: '0.85em', color: '#666' }}>
                                                                {profile.email || 'Usuario'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {profile.bio && (
                                                        <div className={styles.shareBio} title={profile.bio}>
                                                            {profile.bio}
                                                        </div>
                                                    )}
                                                    <p className={styles.shareMessage}>{t('biblionautas.request.myRequests.myMessage', { message: req.message })}</p>
                                                    <div className="card-actions">
                                                        <button onClick={() => handleDelete(req.id)} className={`${styles.btnIcon} delete`} title={t('biblionautas.request.myRequests.cancel')} data-testid={`cancel-request-btn-${req.id}`}>
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Accepted Accesses */}
                            {myAccesses.length > 0 && (
                                <div className="subsection" style={{ marginTop: '1.5rem' }} data-testid="my-accesses-section">
                                    <h5 className={styles.subsectionTitle}>{t('biblionautas.request.myRequests.accessTitle')}</h5>
                                    <div className={styles.shareCardsGrid}>
                                        {myAccesses.map((req) => {
                                            const profile = getProfileData(req)
                                            return (
                                                <div key={req.id} className={styles.shareCard} data-testid={`accepted-access-card-${req.owner_id}`}>
                                                    <div className={styles.shareHeader}>
                                                        <div className="share-user-info">
                                                            <span className="share-name" style={{ fontWeight: 'bold', display: 'block' }}>
                                                                {profile.full_name || 'Usuario'}
                                                            </span>
                                                            <span className={styles.shareEmail} style={{ fontSize: '0.85em', color: '#666' }}>
                                                                {profile.email || 'Usuario'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {profile.bio && (
                                                        <div className={styles.shareBio} title={profile.bio}>
                                                            {profile.bio}
                                                        </div>
                                                    )}
                                                    <div className="card-actions">
                                                        <button onClick={() => onViewLibrary(req.owner_id, profile.full_name || 'Usuario')} className={styles.btnIcon} title={t('biblionautas.request.myRequests.view')} data-testid={`view-library-btn-${req.owner_id}`}>
                                                            👁️
                                                        </button>
                                                        <button onClick={() => handleDelete(req.id)} className={`${styles.btnIcon} delete`} title={t('biblionautas.request.myRequests.leave')} data-testid={`leave-library-btn-${req.id}`}>
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}
                        </>
                    )
                })()}
            </div>

            {/* History Table */}
            <div className={styles.sectionDivider}></div>
            <HistoryTable session={session} mode="biblionaut" key={requests.length} />

            <CustomConfirmModal
                isOpen={!!requestToDelete}
                type="danger"
                title={t('biblionautas.request.modals.cancelTitle')}
                message={t('biblionautas.request.modals.cancelMessage')}
                confirmText={t('biblionautas.request.modals.confirm')}
                onConfirm={confirmDelete}
                onCancel={() => setRequestToDelete(null)}
            />

            <CustomConfirmModal
                isOpen={notification.isOpen}
                type={notification.type}
                title={notification.title}
                message={notification.message}
                showCancel={false}
                confirmText={t('bookList.modals.deleteBook.understood', { defaultValue: 'Entendido' })}
                onConfirm={() => setNotification({ ...notification, isOpen: false })}
            />
        </div>
    )
}
